import { monthlyPayment, type LoanRow } from "./loan";
import { paymentDate } from "./paymentDates";
import type { YMD } from "./dates";

/**
 * The plain-number mortgage maths shared by the Mortgage Calculator page (through mortgageWorking.ts, which re-exports it) and the API
 * (synced to utility-api/core/synced): affordability ratios, the biweekly model and year-by-year cost growth. No UI or table imports.
 */
/* ---------------- affordability (front-end and back-end ratios) ---------------- */

export type DtiLabel = "within" | "stretch" | "above";
export interface Affordability {
  front: number; back: number; label: DtiLabel; message: string;
  /** Housing and total-debt payments that would sit exactly at 28% and 36% of income. */
  maxHousing28: number; maxTotalDebt36: number;
}
/** Common guideline pair: housing at most 28% and all debts at most 36% of gross monthly income. */
export const DTI_GUIDE = { front: 28, back: 36 } as const;
/** FHA's benchmark ratios for manually underwritten loans (HUD Handbook 4000.1): 31% and 43%. Lenders may go above or below. */
export const DTI_STRETCH = { front: 31, back: 43 } as const;

/**
 * Front ratio = housing payment / gross monthly income; back ratio = (housing payment + other monthly debts) / income.
 * "within" = at or under 28/36; "stretch" = over 28/36 but at or under 31/43; "above" = over 31 or 43. Null for no or non-positive income.
 */
export function affordability(grossMonthlyIncome: number | null, housingMonthly: number, otherDebts = 0): Affordability | null {
  if (grossMonthlyIncome == null || !(grossMonthlyIncome > 0) || !Number.isFinite(housingMonthly)) return null;
  const debts = Number.isFinite(otherDebts) && otherDebts > 0 ? otherDebts : 0;
  const front = (housingMonthly / grossMonthlyIncome) * 100;
  const back = ((housingMonthly + debts) / grossMonthlyIncome) * 100;
  const eps = 1e-9;
  const within = front <= DTI_GUIDE.front + eps && back <= DTI_GUIDE.back + eps;
  const stretch = !within && front <= DTI_STRETCH.front + eps && back <= DTI_STRETCH.back + eps;
  const label: DtiLabel = within ? "within" : stretch ? "stretch" : "above";
  const message = within
    ? "Within the common 28/36 guideline."
    : stretch ? "A stretch: above 28/36 but within the 31/43 limits FHA uses as its benchmark. Some lenders approve it, many want more cushion."
      : "Above both common guidelines. Lenders are likely to ask for a lower payment, a larger down payment or less other debt.";
  return { front, back, label, message, maxHousing28: grossMonthlyIncome * 0.28, maxTotalDebt36: grossMonthlyIncome * 0.36 };
}

/* ---------------- biweekly payments ---------------- */

export interface BiweeklyResult {
  /** Half of the monthly principal-and-interest payment, paid every 14 days (26 payments a year). */
  payment: number; payments: number; totalInterest: number;
  /** Against the plain monthly schedule of the same loan (no extra payments). */
  monthlyInterest: number; interestSaved: number; monthlyPayments: number;
  /** Payoff date, or null when no first payment date is given. */
  payoff: YMD | null; monthlyPayoff: YMD | null; monthsSooner: number; yearsLabel: string;
}

export { addDays } from "./paymentDates";
import { addDays } from "./paymentDates";

/**
 * Biweekly model: pay half of the monthly P&I every 14 days. Interest accrues every 14 days at annual rate / 26.
 * This is the common calculator model; some servicers only post biweekly payments once a month, in which case
 * the saving is smaller, so the page tells people to ask their lender. Principal and interest only (no taxes or insurance).
 */
export function biweeklyPlan(principal: number, annualPct: number, months: number, first: YMD | null): BiweeklyResult | null {
  if (!(principal > 0) || !(months >= 1) || !Number.isFinite(annualPct) || annualPct < 0) return null;
  const monthly = monthlyPayment(principal, annualPct, months);
  const pay = monthly / 2, r = annualPct / 100 / 26;
  let bal = principal, interestSum = 0, n = 0;
  const cap = Math.ceil(months * 26 / 12) + 5;
  while (bal > 0.005 && n < cap) {
    n++;
    const interest = bal * r;
    const principalPart = Math.min(bal, pay - interest);
    interestSum += interest;
    bal -= principalPart;
  }
  // Monthly baseline interest, same loan with no extras.
  const monthlyInterest = monthly * Math.round(months) - principal;
  const monthlyPayments = Math.round(months);
  const monthsSooner = Math.max(0, Math.round(monthlyPayments - (n * 14) / 30.4375));
  const payoff = first ? addDays(first, 14 * (n - 1)) : null;
  const yearsLabel = (() => {
    const y = Math.floor(monthsSooner / 12), mo = monthsSooner % 12;
    return [y ? `${y} year${y === 1 ? "" : "s"}` : "", mo ? `${mo} month${mo === 1 ? "" : "s"}` : ""].filter(Boolean).join(" ") || "0 months";
  })();
  return {
    payment: pay, payments: n, totalInterest: interestSum, monthlyInterest, interestSaved: Math.max(0, monthlyInterest - interestSum), monthlyPayments,
    payoff, monthlyPayoff: first ? paymentDate(first, monthlyPayments - 1) : null, monthsSooner, yearsLabel,
  };
}

/* ---------------- cost growth ---------------- */

export interface CostGrowthInput {
  /** Monthly amounts in year 1. */
  tax: number; insurance: number; hoa: number; other: number;
  /** Yearly increases in percent. `other` grows with the insurance rate (a general-cost rate), so it has no field of its own. */
  taxGrowthPct: number; insGrowthPct: number; hoaGrowthPct: number;
}
export interface CostYearRow {
  year: number; payments: number; taxMonthly: number; insMonthly: number; hoaMonthly: number; otherMonthly: number; pmiMonthly: number;
  /** Average monthly total for the year: P&I + the lines above. */
  avgMonthly: number; yearTotal: number;
}
export const r2 = (n: number) => Math.round(n * 100) / 100;
const grow = (base: number, pct: number, year: number) => base * Math.pow(1 + pct / 100, year - 1);

/** Year-by-year monthly costs: P&I from the schedule, taxes/insurance/HOA/other grown each loan year, PMI as it drops off. */
export function costGrowth(rows: LoanRow[], g: CostGrowthInput): CostYearRow[] {
  const out: CostYearRow[] = [];
  for (const row of rows) {
    const y = Math.ceil(row.month / 12);
    if (!out[y - 1]) out[y - 1] = { year: y, payments: 0, taxMonthly: 0, insMonthly: 0, hoaMonthly: 0, otherMonthly: 0, pmiMonthly: 0, avgMonthly: 0, yearTotal: 0 };
    const o = out[y - 1];
    o.payments++;
    o.yearTotal += row.payment + row.pmi;
    o.pmiMonthly = Math.max(o.pmiMonthly, row.pmi);
  }
  for (const o of out) {
    o.taxMonthly = r2(grow(g.tax, g.taxGrowthPct, o.year));
    o.insMonthly = r2(grow(g.insurance, g.insGrowthPct, o.year));
    o.hoaMonthly = r2(grow(g.hoa, g.hoaGrowthPct, o.year));
    o.otherMonthly = r2(grow(g.other, g.insGrowthPct, o.year));
    o.yearTotal = r2(o.yearTotal + (o.taxMonthly + o.insMonthly + o.hoaMonthly + o.otherMonthly) * o.payments);
    o.avgMonthly = r2(o.yearTotal / o.payments);
  }
  return out;
}
